import { api } from './client';

export interface SubcontractorVendor {
  id: string;
  legacyId: number | null;
  name: string;
}

export interface SubcontractorWork {
  id: string;
  legacyId: number | null;
  clientId: number | null;
  clientName: string;
  legacyRekiId: number | null;
  vendorId: string | null;
  vendor: SubcontractorVendor | null;
  vendorName: string | null;
  legacyVendorId: number | null;
  workName: string;
  vendorCost: string | null;
  clientCost: string | null;
  clientInvoice: string | null;
  offerFileName: string | null;
  hasOfferFile: boolean;
  legacyOfferFilePath: string | null;
  vendorDone: boolean;
  comment: string | null;
  vendorDocsFileName: string | null;
  hasVendorDocsFile: boolean;
  legacyVendorDocsFilePath: string | null;
  vendorPayment: boolean;
  vendorDocsReceived: boolean;
  vendorDocsFinished: boolean;
  isArchived: boolean;
  legacyCreatedAt: string | null;
  legacyLastChange: string | null;
  legacyLog: string | null;
  importedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubcontractorWorkAuditLog {
  id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  modelName: string;
  recordId: string;
  userId: string | null;
  userName: string | null;
  dataBefore: Record<string, unknown> | null;
  dataAfter: Record<string, unknown> | null;
  diff: Record<string, { old: unknown; new: unknown }> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface SaveSubcontractorWorkDto {
  id?: string;
  clientId: number;
  workName: string;
  vendorId?: string;
  vendorCost?: string;
  clientCost?: string;
  clientInvoice?: string;
  vendorDone?: boolean;
  comment?: string;
  vendorPayment?: boolean;
  vendorDocsReceived?: boolean;
  vendorDocsFinished?: boolean;
  isArchived?: boolean;
}

function toFormData(
  dto: SaveSubcontractorWorkDto,
  files?: { offerFile?: Blob | null; vendorDocsFile?: Blob | null },
): FormData {
  const formData = new FormData();
  Object.entries(dto).forEach(([key, value]) => {
    if (key !== 'id' && value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
  if (files?.offerFile) formData.append('offerFile', files.offerFile, 'offer');
  if (files?.vendorDocsFile) formData.append('vendorDocsFile', files.vendorDocsFile, 'vendor-docs');
  return formData;
}

const multipartConfig = {
  headers: { 'Content-Type': 'multipart/form-data' },
};

async function getVendors(): Promise<SubcontractorVendor[]> {
  const { data } = await api.get('/subcontractor-works/vendors');
  return data.items || [];
}

export const subcontractorWorksApi = {
  getAll: async (
    status: 'active' | 'archived' | 'all' = 'active',
  ): Promise<{ entries: SubcontractorWork[]; vendors: SubcontractorVendor[] }> => {
    const [worksResponse, vendors] = await Promise.all([
      api.get('/subcontractor-works', {
        params: { status, ownershipFilter: 'all' },
      }),
      getVendors(),
    ]);

    return {
      entries: worksResponse.data.items || [],
      vendors,
    };
  },

  save: async (
    dto: SaveSubcontractorWorkDto,
    files?: { offerFile?: Blob | null; vendorDocsFile?: Blob | null },
  ) => {
    const isUpdate = Boolean(dto.id);
    const response = isUpdate
      ? await api.patch(`/subcontractor-works/${dto.id}`, toFormData(dto, files), multipartConfig)
      : await api.post('/subcontractor-works', toFormData(dto, files), multipartConfig);

    return {
      success: true,
      id: response.data.id as string,
      action: isUpdate ? 'updated' as const : 'created' as const,
    };
  },

  getHistory: async (id: string) => {
    const { data } = await api.get(`/subcontractor-works/${id}/history`);
    return data as { items: SubcontractorWorkAuditLog[]; total: number };
  },

  getFileBlobUrl: async (id: string, kind: 'offer' | 'vendor-docs'): Promise<string> => {
    const response = await api.get(`/subcontractor-works/${id}/files/${kind}`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },
};
